import React, { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function AdminLogin() {
  const { user, login, checking } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const location = useLocation();

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-charcoal">Checking session…</div>;
  }
  if (user) {
    const dest = location.state?.from?.pathname || "/admin";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg || "").join(" ") : "Login failed.";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-ivory text-charcoal flex flex-col">
      <div className="border-b border-ivory-400 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center">
          <Link to="/" className="font-serif text-2xl text-forest-900">
            Incent<span className="text-brass">Tax</span> <span className="text-charcoal-light text-base">/ admin</span>
          </Link>
        </div>
      </div>
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="w-full max-w-md bg-white border border-ivory-400 p-10">
          <ShieldAlert className="text-brass mb-6" size={32} strokeWidth={1.5} />
          <h1 className="font-serif text-4xl text-forest-900 mb-3">Sign in</h1>
          <p className="text-charcoal mb-8 text-sm">Access the IncentTax leads dashboard.</p>

          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="block text-forest-700 uppercase tracking-[0.2em] text-xs mb-3">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
              />
            </label>
            <label className="block">
              <span className="block text-forest-700 uppercase tracking-[0.2em] text-xs mb-3">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
              />
            </label>
            {err && (
              <div data-testid="admin-login-error" className="text-sm text-red-700 border border-red-200 bg-red-50 px-4 py-3">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-6 py-3 font-medium transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : null}
              Sign in
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
